/**
 * Convert Experiences cart line-item tracking.
 * Stamps `_convert-test` / `_convert-variation` onto add-to-cart so each product
 * line can be attributed to the most relevant test on the page it was added from.
 * Loaded from `snippets/vendor-scripts.liquid` (do not Yottaa-sequence).
 */
(function initConvertCartTracking() {
  if (window.__convertCartTracking) return;
  window.__convertCartTracking = true;

  const PROPERTY_TEST = "_convert-test";
  const PROPERTY_VARIATION = "_convert-variation";

  function indexConvertById(listOrMap) {
    const out = {};
    if (!listOrMap) return out;
    if (Array.isArray(listOrMap)) {
      listOrMap.forEach((item) => {
        if (item && item.id != null) out[String(item.id)] = item;
      });
      return out;
    }
    if (typeof listOrMap === "object") {
      Object.keys(listOrMap).forEach((key) => {
        const item = listOrMap[key];
        out[String((item && item.id) || key)] = item;
      });
    }
    return out;
  }

  function collectConvertUrlRules(node, acc) {
    const rules = acc || [];
    if (!node) return rules;
    if (Array.isArray(node)) {
      node.forEach((child) => collectConvertUrlRules(child, rules));
      return rules;
    }
    if (typeof node !== "object") return rules;
    const ruleType = node.rule_type;
    const matchType = node.matching?.match_type || node.match_type;
    if (node.value != null && (ruleType === "url" || (ruleType == null && matchType))) {
      rules.push({
        value: node.value,
        matchType: matchType || "contains",
        negated: !!(node.matching?.negated || node.negated)
      });
    }
    Object.keys(node).forEach((key) => {
      if (key === "matching") return;
      collectConvertUrlRules(node[key], rules);
    });
    return rules;
  }

  function currentPageUrlParts() {
    const href = String(window.location.href || "");
    const pathname = String(window.location.pathname || "");
    const origin = String(window.location.origin || "");
    const originPath = origin + pathname;
    const hrefNoQuery = href.split("#")[0].split("?")[0];
    return {
      href: href.toLowerCase(),
      hrefNoQuery: hrefNoQuery.toLowerCase().replace(/\/$/, ""),
      originPath: originPath.toLowerCase().replace(/\/$/, ""),
      pathname: pathname.toLowerCase().replace(/\/$/, "")
    };
  }

  function normalizeConvertUrl(value) {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    try {
      const parsed = new URL(raw, window.location.origin);
      parsed.hash = "";
      parsed.search = "";
      parsed.hostname = parsed.hostname.replace(/^www\./, "");
      return parsed.href.replace(/\/$/, "");
    } catch {
      return raw.replace(/\/$/, "");
    }
  }

  function convertUrlRuleResult(rule, page) {
    const value = String(rule.value || "").trim();
    if (!value) return { matched: false, score: 0 };
    const matchType = String(rule.matchType || "contains").toLowerCase();
    const needle = value.toLowerCase();
    let matched = false;
    let score = 0;

    if (matchType === "matches" || matchType === "equals" || matchType === "exact" || matchType === "is") {
      const normalizedRule = normalizeConvertUrl(value);
      const exactCandidates = [page.hrefNoQuery, page.originPath, normalizeConvertUrl(page.hrefNoQuery)];
      if (normalizedRule && exactCandidates.indexOf(normalizedRule) !== -1) {
        matched = true;
        score = 100;
      } else {
        try {
          const rulePath = new URL(value, window.location.origin).pathname.toLowerCase().replace(/\/$/, "");
          if (rulePath && rulePath === page.pathname) {
            matched = true;
            score = 90;
          }
        } catch {
          if (needle.replace(/\/$/, "") === page.pathname) {
            matched = true;
            score = 90;
          }
        }
      }
    } else if (matchType === "regex" || matchType === "matches_regex" || matchType === "regexp") {
      try {
        const re = new RegExp(value, "i");
        if (re.test(page.href) || re.test(page.originPath) || re.test(page.pathname)) {
          matched = true;
          score = 70;
        }
      } catch {
        matched = false;
      }
    } else if (matchType === "starts_with" || matchType === "begins_with" || matchType === "prefix") {
      const haystacks = [page.href, page.originPath, page.pathname];
      if (haystacks.some((hay) => hay.indexOf(needle) === 0)) {
        matched = true;
        score = 60 + Math.min(30, needle.length);
      }
    } else {
      const haystacks = [page.href, page.originPath, page.pathname];
      if (haystacks.some((hay) => hay.indexOf(needle) !== -1)) {
        matched = true;
        score = 40 + Math.min(50, needle.length);
      }
    }

    if (rule.negated) {
      return matched ? { matched: false, excluded: true, score: 0 } : { matched: false, score: 0 };
    }
    return { matched, score: matched ? score : 0 };
  }

  function scoreConvertLocation(location, page) {
    const urlRules = collectConvertUrlRules(location && location.rules, []);
    if (!urlRules.length) return { matched: false, unknown: true, score: 0 };

    let best = 0;
    let anyMatched = false;
    let excluded = false;
    urlRules.forEach((rule) => {
      const result = convertUrlRuleResult(rule, page);
      if (result.excluded) excluded = true;
      if (result.matched) {
        anyMatched = true;
        if (result.score > best) best = result.score;
      }
    });

    if (anyMatched) return { matched: true, score: best };
    if (excluded) return { matched: false, excluded: true, score: 0 };
    return { matched: false, score: 0 };
  }

  function scoreConvertExperience(expId, currentExp, dataExp, locationsById, page) {
    const variation = currentExp && currentExp.variation;
    const variationId = variation?.id || variation?.key;
    if (!variationId) return null;

    const candidate = {
      experienceId: String(expId),
      variationKey: String(variationId),
      firstTime: currentExp.firstTime === true,
      score: 5
    };

    if (!dataExp) {
      candidate.score = 8;
      return candidate;
    }

    const locationIds = Array.isArray(dataExp.locations) ? dataExp.locations : dataExp.locations != null ? [dataExp.locations] : [];
    if (!locationIds.length) {
      candidate.score = 12;
      return candidate;
    }

    let bestLocationScore = 0;
    let anyMatch = false;
    let resolvedCount = 0;
    let missingCount = 0;

    locationIds.forEach((locationId) => {
      const location = locationsById[String(locationId)];
      if (!location) {
        missingCount += 1;
        return;
      }
      resolvedCount += 1;
      const locationScore = scoreConvertLocation(location, page);
      if (locationScore.unknown) missingCount += 1;
      if (locationScore.matched) {
        anyMatch = true;
        if (locationScore.score > bestLocationScore) bestLocationScore = locationScore.score;
      }
    });

    if (anyMatch) {
      candidate.score = 50 + bestLocationScore;
      return candidate;
    }
    if (missingCount && !resolvedCount) {
      candidate.score = 8;
      return candidate;
    }
    if (missingCount) {
      candidate.score = 7;
      return candidate;
    }
    candidate.score = 3;
    return candidate;
  }

  // Rank tests in currentData by Convert location URL rules for this page. If locations
  // are missing or do not match, still record a running test instead of dropping Convert.
  function getConvertTestData() {
    try {
      const convert = window.convert;
      const currentExperiences = convert?.currentData?.experiences;
      if (!currentExperiences) return null;
      const expIds = Object.keys(currentExperiences);
      if (!expIds.length) return null;

      const experiencesById = indexConvertById(convert.data?.experiences);
      const locationsById = indexConvertById(convert.data?.locations);
      const page = currentPageUrlParts();

      let best = null;
      expIds.forEach((expId) => {
        const scored = scoreConvertExperience(
          expId,
          currentExperiences[expId],
          experiencesById[String(expId)],
          locationsById,
          page
        );
        if (!scored) return;
        if (
          !best ||
          scored.score > best.score ||
          (scored.score === best.score && scored.firstTime && !best.firstTime)
        ) {
          best = scored;
        }
      });

      if (!best) return null;
      return { experienceId: best.experienceId, variationKey: best.variationKey };
    } catch {
      return null;
    }
  }

  function isCartAddForm(form) {
    if (!form || !(form instanceof HTMLFormElement)) return false;
    const action = form.action || form.getAttribute("action") || "";
    return action.includes("/cart/add");
  }

  function isCartAddUrl(value) {
    return String(value || "").indexOf("/cart/add") !== -1;
  }

  function ensureConvertInput(form, name, value) {
    let input = form.querySelector(`input[name="properties[${name}]"]`);
    if (input) {
      input.value = value;
    } else {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${name}]`;
      input.value = value;
      form.appendChild(input);
    }
  }

  function tagItemWithConvert(item, data) {
    if (!item || typeof item !== "object") return false;
    const properties = item.properties && typeof item.properties === "object" ? item.properties : {};
    if (properties._gwp_managed || properties._bfcm_promo) return false;
    let changed = false;
    if (!Object.prototype.hasOwnProperty.call(properties, PROPERTY_TEST)) {
      properties[PROPERTY_TEST] = data.experienceId;
      changed = true;
    }
    if (!Object.prototype.hasOwnProperty.call(properties, PROPERTY_VARIATION)) {
      properties[PROPERTY_VARIATION] = data.variationKey;
      changed = true;
    }
    if (changed) item.properties = properties;
    return changed;
  }

  function tagAddJsonBody(parsed, data) {
    if (!parsed || typeof parsed !== "object") return false;
    if (Array.isArray(parsed.items)) {
      let changed = false;
      parsed.items.forEach((item) => {
        if (tagItemWithConvert(item, data)) changed = true;
      });
      return changed;
    }
    if (parsed.id != null) {
      return tagItemWithConvert(parsed, data);
    }
    return false;
  }

  function tagKeyValueBody(body, data) {
    if (!body || typeof body.has !== "function" || typeof body.set !== "function") return false;

    const propertyPrefixes = [];
    let changed = false;
    if (body.has("id")) propertyPrefixes.push("properties");

    if (typeof body.keys === "function") {
      for (const key of body.keys()) {
        const match = String(key).match(/^items\[(\d+)\]\[id\]$/);
        if (match) propertyPrefixes.push(`items[${match[1]}][properties]`);
      }
    }

    propertyPrefixes.forEach((prefix) => {
      if (body.has(`${prefix}[_gwp_managed]`) || body.has(`${prefix}[_bfcm_promo]`)) return;
      const testKey = `${prefix}[${PROPERTY_TEST}]`;
      const variationKey = `${prefix}[${PROPERTY_VARIATION}]`;
      if (!body.has(testKey)) {
        body.set(testKey, data.experienceId);
        changed = true;
      }
      if (!body.has(variationKey)) {
        body.set(variationKey, data.variationKey);
        changed = true;
      }
    });

    return changed;
  }

  function tagRequestBody(body, data) {
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      return { body, changed: tagKeyValueBody(body, data) };
    }
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      return { body, changed: tagKeyValueBody(body, data) };
    }
    if (typeof body !== "string") return { body, changed: false };

    const trimmed = body.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(body);
        if (tagAddJsonBody(parsed, data)) {
          return { body: JSON.stringify(parsed), changed: true };
        }
      } catch (err) {}
      return { body, changed: false };
    }

    const params = new URLSearchParams(body);
    return tagKeyValueBody(params, data)
      ? { body: params.toString(), changed: true }
      : { body, changed: false };
  }

  function fetchArgUrl(input) {
    if (typeof input === "string") return input;
    if (typeof Request !== "undefined" && input instanceof Request) return input.url;
    if (input && typeof input.url === "string") return input.url;
    return "";
  }

  function stampCartAddForms() {
    const data = getConvertTestData();
    if (!data) return false;
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      ensureConvertInput(form, PROPERTY_TEST, data.experienceId);
      ensureConvertInput(form, PROPERTY_VARIATION, data.variationKey);
    });
    return true;
  }

  document.addEventListener(
    "submit",
    function (e) {
      if (!isCartAddForm(e.target)) return;
      const data = getConvertTestData();
      if (data) {
        ensureConvertInput(e.target, PROPERTY_TEST, data.experienceId);
        ensureConvertInput(e.target, PROPERTY_VARIATION, data.variationKey);
      }
    },
    true
  );

  // Populate existing forms as soon as Convert finishes evaluating, without holding
  // a shopper's cart request while tracking becomes ready.
  let refreshAttempts = 0;
  const refreshTimer = window.setInterval(() => {
    refreshAttempts += 1;
    if (stampCartAddForms() || refreshAttempts >= 20) window.clearInterval(refreshTimer);
  }, 250);
  stampCartAddForms();

  // Theme AJAX paths can bypass native form submission. Stamp the common
  // Cart API body formats synchronously, then send the request immediately.
  if (typeof window.fetch === "function") {
    const convertOriginalFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        if (init && isCartAddUrl(fetchArgUrl(input))) {
          const data = getConvertTestData();
          if (data && Object.prototype.hasOwnProperty.call(init, "body")) {
            const tagged = tagRequestBody(init.body, data);
            if (tagged.changed) {
              return convertOriginalFetch.call(this, input, Object.assign({}, init, { body: tagged.body }));
            }
          }
        }
      } catch (err) {}
      return convertOriginalFetch.apply(this, arguments);
    };
  }
})();
